import { Injectable } from '@angular/core';
import { defer, distinctUntilChanged, map, merge, Observable, shareReplay } from 'rxjs';
import { Permission } from '@app/interfaces/user';
import { AuthService } from './auth.service';
import { ClientDataStoreService } from './client-data-store.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  readonly permissions$: Observable<readonly Permission[]>;

  constructor(auth: AuthService, clientStore: ClientDataStoreService) {
    this.permissions$ = defer(() => {
      // The selected session owns permissions; an OIDC claim must never fall back to DNA.
      if (auth.isOidcMode()) {
        return auth.oidc.permissionNames$.pipe(map(names =>
          Object.values(Permission).filter((value): value is Permission =>
            typeof value === 'number' && names.includes(Permission[value]))
        ));
      }

      return merge(
        clientStore.clientSummary$.pipe(map(summary => summary.Permissions || [])),
        auth.LogOutEvent.pipe(map(() => [] as Permission[])),
      );
    }).pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  hasPermission$(permission: Permission): Observable<boolean> {
    return this.permissions$.pipe(
      map(permissions => permissions.includes(permission)),
      distinctUntilChanged(),
    );
  }
}
